import React, { lazy, Suspense } from "react";
import { createRoot } from "react-dom/client";
import { Provider, useDispatch, useSelector } from "react-redux";
import { PersistGate } from "redux-persist/integration/react";
import CustomRouter from "./Components/CustomRouter";
import { Amplify } from "aws-amplify";
import { amplifyConfiguration } from "./amplify-config";
import { GlobalStyle, themeBuilder } from "./global-styles";
import { store, persistor, history } from "./store";
import { ThemeProvider } from "styled-components";
import { SET_THEME } from "./store/actions/constants";
import "survey-core/defaultV2.min.css";
import "survey-core/survey.i18n";
import loadIcons from "./icons";
import { addMetaTag, runTranslateFix } from "./utilities";
import LoaderOverlay from "./Components/LoaderOverlay";
import { TranslationProvider } from "react-auto-google-translate";
const App = lazy(() => import(/* webpackChunkName: "App" */ "./App"));

Amplify.configure(amplifyConfiguration);

// Intercept QR device-login URLs and redirect to the native app before rendering the web UI.
// When the user scans the QR code in Settings, the URL is:
//   https://{host}?ticket=<id>&account_id=<id>&cognito_id=<id>
// Attempting the custom-scheme redirect hands off to the Eleplan iOS/Android app (if installed)
// without the user ever seeing the web login page.
//
// The /auto-login path is intentionally excluded: it is used for mobile→web handoff (e.g. billing
// deep links) where the user should land on the web app, not be redirected back to the native app.
{
  const _p = new URLSearchParams(window.location.search);
  const _ticket = _p.get('ticket');
  const _accountId = _p.get('account_id');
  const _cognitoId = _p.get('cognito_id');
  const _isAutoLogin = window.location.pathname === '/auto-login';
  if (_ticket && _accountId && _cognitoId && !_isAutoLogin) {
    window.location.href = `com.eleplan.app://login-device?ticket=${encodeURIComponent(_ticket)}&account_id=${encodeURIComponent(_accountId)}&cognito_id=${encodeURIComponent(_cognitoId)}`;
  }
}

const root = createRoot(document.getElementById("root"));
loadIcons();
addMetaTag();
runTranslateFix();

const ThemeListener = ({ children }) => {
	const user = useSelector((globalStore) => globalStore.authentication.user);
	const customer = useSelector((globalStore) => globalStore.session.customer);
	const whitelabel = user.whitelabel;
	const coupon = customer?.discount?.coupon;
	const brand_primary = whitelabel?.brand_primary || coupon?.metadata?.brand_primary;
	const brand_secondary = whitelabel?.brand_secondary || coupon?.metadata?.brand_secondary;
	const theme = useSelector((globalStore) => globalStore.session.theme);
	const dark = window.matchMedia("(prefers-color-scheme: dark)");
	const dispatch = useDispatch();
	const themeObj = themeBuilder(theme, brand_primary, brand_secondary)?.buildTheme;
	
	dark.addEventListener("change", (e) => {
		dispatch({ type: SET_THEME, payload: (e.matches ? "dark" : theme || "default") });
	});

	return (
		<ThemeProvider theme={themeObj || {}}>
			{children}
		</ThemeProvider>
	);
};

root.render(
	<Provider store={store}>
		<ThemeListener>
			<PersistGate
				loading={null}
				persistor={persistor}>
				<CustomRouter basename="/" history={history}>
					<GlobalStyle />
					<Suspense fallback={<LoaderOverlay message="Authorizing" />}>
						<TranslationProvider originalLang="en">
							<App />
						</TranslationProvider>
					</Suspense>
				</CustomRouter>
			</PersistGate>
		</ThemeListener>
	</Provider>
);